import React from 'react';
import InitialAvatar from '../InitialAvatar';
import './OrderCommentsButton.css';

const formatCount = (n) => (n > 99 ? '99+' : String(n));

/** How many live faces fit on the comment icon before we collapse to "+N". */
const MAX_LIVE_FACES = 2;

/**
 * Orders INFO column: the comments (chat) icon, to the right of Order History.
 * Wrapped in <HoverPO hideBadge> so a click opens the same docked comment thread
 * the old Notes-column expand button opened.
 *
 * Badge: blue = comments you have not read (same number as the bell and the tray chat),
 * otherwise yellow = total comments on the line, as the Notes column showed.
 *
 * presenceUsers: people who currently have this chat open. Drawn as a tiny face
 * stuck to the bubble, so inline-mode presence can keep the full avatar beside it.
 */
const OrderCommentsButton = React.forwardRef(function OrderCommentsButton(
    { total = 0, unread = 0, active = false, presenceUsers = [], onClick, style, className = '' },
    ref
) {
    const watchers = (presenceUsers || []).filter((user) => user && (user.name || user.email));
    const shown = watchers.slice(0, MAX_LIVE_FACES);
    const extra = watchers.length - shown.length;
    const watcherNames = watchers.map((user) => user.name || user.email);
    const watchingLabel = watcherNames.length
        ? ` ${watcherNames.join(', ')} ${watcherNames.length === 1 ? 'is' : 'are'} in this chat.`
        : '';
    const label = (unread > 0
        ? `Comments: ${unread} new of ${total || unread}`
        : total > 0
            ? `Comments: ${total}`
            : 'Comments: none yet. Click to add one.') + watchingLabel;

    return (
        <button
            ref={ref}
            type="button"
            className={`order-comments-icon-btn${unread > 0 ? ' has-unread' : ''}${active ? ' is-active' : ''} ${className}`.trim()}
            onClick={onClick}
            title={label}
            aria-label={label}
            aria-pressed={active}
            style={style}
        >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {unread > 0 ? (
                <span className="order-comments-badge is-unread" aria-hidden="true">{formatCount(unread)}</span>
            ) : total > 0 ? (
                <span className="order-comments-badge" aria-hidden="true">{formatCount(total)}</span>
            ) : null}
            {shown.length > 0 && (
                <span className="order-comments-live" aria-hidden="true">
                    {shown.map((user) => (
                        <span key={user.email || user.name} className="order-comments-live-avatar">
                            <InitialAvatar
                                name={user.name || user.email}
                                size={16}
                                useStoredAvatar={false}
                                style={{ fontSize: 8, lineHeight: '16px' }}
                            />
                        </span>
                    ))}
                    {extra > 0 && (
                        <span className="order-comments-live-more">+{extra > 9 ? 9 : extra}</span>
                    )}
                </span>
            )}
        </button>
    );
});

export default OrderCommentsButton;
